import type { CurrentUser } from './api';
import { AuthApiError } from './errors';

export type AuthState =
  | { status: 'loading' }
  /** `notice` is set when the server turned down a session the browser thought was live. */
  | { status: 'signed-out'; notice?: string }
  | { status: 'authorized'; user: CurrentUser }
  /** `reason` is the server's: `not_set_up` (no user row) or `deactivated`. */
  | { status: 'forbidden'; email: string | undefined; reason?: string }
  | { status: 'error'; message: string };

export interface SessionDeps {
  getToken(): Promise<string | null>;
  fetchMe(): Promise<CurrentUser>;
  signOut(): Promise<void>;
}

const CHECK_FAILED = 'We could not check your access to Mothership. Please try again.';

/**
 * What the browser should show: no token means signed out; /api/me then
 * decides. 200 is in, 403 is signed in but not let in (no active user row), 401 is a
 * session the server refuses (so it is dropped and the visitor signs in again),
 * and anything else (503, a network failure) is an error worth retrying.
 */
export async function resolveSession(deps: SessionDeps): Promise<AuthState> {
  try {
    if ((await deps.getToken()) === null) {
      return { status: 'signed-out' };
    }
    return { status: 'authorized', user: await deps.fetchMe() };
  } catch (error) {
    if (error instanceof AuthApiError) {
      if (error.status === 401) {
        await dropSession(deps);
        return { status: 'signed-out', notice: error.message };
      }
      if (error.status === 403) {
        return { status: 'forbidden', email: error.email, ...(error.reason ? { reason: error.reason } : {}) };
      }
    }
    return { status: 'error', message: CHECK_FAILED };
  }
}

/** Best effort: the visitor is treated as signed out even when the sign-out request fails. */
async function dropSession(deps: SessionDeps): Promise<void> {
  try {
    await deps.signOut();
  } catch {
    // Nothing more to do; the next load asks the server again.
  }
}

/** What the "Not authorized" screen says, by why the server refused (auth.mjs). */
export function forbiddenText(email: string | undefined, reason: string | undefined): string {
  const who = email ?? 'This account';
  if (reason === 'deactivated') return `${who} has had its access to Mothership turned off. Ask an admin if you need it back.`;
  if (reason === 'not_set_up') return `${who} isn't set up in Mothership yet. Ask an admin to add you in Setup → Users.`;
  return `${who} doesn't have access to Mothership. Ask an admin for access.`;
}
